import React, {
  useRef,
  useState,
  useEffect,
  RefObject,
  forwardRef,
  useImperativeHandle,
} from 'react';
import MapIcon from '@mui/icons-material/Map';
import { useDispatch, useSelector } from 'react-redux';
import SettingsIcon from '@mui/icons-material/Settings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box, Grid, Menu, Button, Tooltip, IconButton } from '@mui/material';

import FilterComponent from 'components/Filters/FilterComponent';
import TechnologyBandSelector from '../Filters/TechnologyBandSelector';
import SettingsFilters, { SettingsFiltersRef } from './SettingsFilters';
import SearchInput, { SearchInputRef } from './SearchFilter/SearchInput';
import GranularityDateRangePicker, {
  Ref as GranularityDateRangePickerRef,
} from 'components/Common/GranularityDateRangePicker/GranularityDateRangePicker';

import {
  setGranularityDates,
  setSelectedBands,
} from 'features/advancedMapState';
import { LayersRef } from '../Layers/Layers';
import { RootState } from 'types/types';
import {
  granularityOptions,
  calendarFilterOptions,
} from 'components/Common/GranularityDateRangePicker/helper';
import { CustomGeoJsonLayerData, FilterData, FilterDataValue, GlobalDateFilter } from 'types/types';
import { SearchSuggestionsRef } from './SearchFilter/SearchSuggestions';

interface Layer {
  filter: string;
  cmData: any; // Replace 'any' with the actual type if known
}

interface GroupedLayers {
  [label: string]: Layer[];
}

interface TopFiltersProps {
  markersList: any[];
  dashboardId: string;
  granularityDates: any;
  bandsList: FilterDataValue[];
  initialFilters: FilterData[];
  applySettingsClicked: () => void;
  mapPopupStateVisibility: boolean;
  globalLayersControlRef: RefObject<LayersRef>;
  applyRegionSelection: (flyto: Boolean) => void;
  searchSuggestionsRef: React.RefObject<SearchSuggestionsRef>;
  customGeoJsonLayers?: CustomGeoJsonLayerData[];
}

export interface TopFiltersRef {
  layersIconDetails: {
    className: string;
    tootltipText: string;
  };
  handleChangeLayersIconDetails: React.Dispatch<
    React.SetStateAction<{
      className: string;
      tootltipText: string;
    }>
  >;
  searchInputRef: React.RefObject<SearchInputRef>;
}

const TopFilters: React.ForwardRefRenderFunction<
  TopFiltersRef,
  TopFiltersProps
> = (
  {
    bandsList,
    dashboardId,
    markersList,
    initialFilters,
    globalLayersControlRef,
    granularityDates,
    applySettingsClicked,
    searchSuggestionsRef,
    applyRegionSelection,
    mapPopupStateVisibility,
    customGeoJsonLayers,
  },
  ref
) => {
  const dispatch = useDispatch();

  const selectedBands = useSelector(
    (state: RootState) => state.advancedMap.selectedBands
  );

  const toggleMenu = useSelector(
    (state: RootState) => state.applicationState.toggle
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [regionsMenu, setRegionsMenu] = useState<boolean>(false);
  const [geoJsonLayersIconDetails, setGeoJsonLayersIconDetails] = useState<{
    className: string;
    tootltipText: string;
  }>({ className: '', tootltipText: '' });

  const granularityDateRangePickerRef =
    useRef<GranularityDateRangePickerRef>(null);
  const settingsRef = useRef<SettingsFiltersRef>(null);
  const searchInputRef = useRef<SearchInputRef>(null);

  const values = useRef<any>({
    selectedGranularity: '15min',
    dateTo: new Date(),
    dateFrom: new Date(),
    selectedFilterOption: 'Last 15min',
    selectedValuesCustom: {
      startTime: { hour: 0, minute: 0 },
      endTime: { hour: 23, minute: 45 },
    },
    customFilteredEnteredValues: { daysFrom: 4, daysTo: 2 },
  });

  const handleOnGeoJSONLayersFilterClick = () => {
    globalLayersControlRef?.current?.openLayerControl((prev) => !prev);
  };

  const handleBandToggle = (bandFilterItem: FilterDataValue) => {
    const isSelected = selectedBands.includes(bandFilterItem.id!);
    const updatedSelectedBands = isSelected
      ? selectedBands.filter((id) => id !== bandFilterItem.id!)
      : [...selectedBands, bandFilterItem.id!];
    dispatch(setSelectedBands(updatedSelectedBands));
  };

  const toggleRegions = (e) => {
    setRegionsMenu(true);
    globalLayersControlRef?.current?.openLayerControl(false);
    setAnchorEl(e.currentTarget);
  };

  const closeSettings = () => {
    settingsRef.current?.handleOpenSettingsMenu(false);
    setRegionsMenu(false);
    setAnchorEl(null);
  };

  const handleGranularityDatePickerApply = (body: GlobalDateFilter) => {
    dispatch(setGranularityDates(body));
  };

  useImperativeHandle(ref, () => ({
    handleChangeLayersIconDetails: setGeoJsonLayersIconDetails,
    layersIconDetails: geoJsonLayersIconDetails,
    searchInputRef: searchInputRef,
  }));

  useEffect(() => {
    if (bandsList.length === 0) return;
    // initialize default state BAND selection
    // by default assuming all BANDs are selected
    let toAdd: string[] =
      bandsList
        .filter((toAdd) => !selectedBands.includes(toAdd.id!))
        .map((toAdd) => toAdd.id!) || [];
    if (toAdd.length > 0)
      dispatch(setSelectedBands([...selectedBands, ...toAdd]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandsList]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
      }}
      className={toggleMenu ? 'top-filters-open' : 'top-filters'}
    >
      <div className="common-top-filters">
        <Box className={'bands-filters'}>
          <Box className="relative-pos">
            <Box className="bands-main">
              <IconButton
                color="inherit"
                onClick={(e) => toggleRegions(e)}
                className="big-button"
              >
                <LocationOnIcon className="big-button-icon" />
              </IconButton>
            </Box>
            {regionsMenu && (
              <Menu
                anchorEl={anchorEl}
                open={regionsMenu}
                onClose={closeSettings}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                className="filters-menu-container"
              >
                <Box
                  className={
                    'filters-menu large-map-filters region-selection-main'
                  }
                >
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      sx={{ mb: 2 }}
                      className={'filters-container'}
                    >
                      {initialFilters
                        .filter((filter) => filter.category === 'regionName')
                        .map((filter, i) => (
                          <Grid
                            item
                            key={i}
                            xs={
                              12 /
                              initialFilters.filter(
                                (filter) => filter.category === 'regionName'
                              ).length
                            }
                          >
                            <FilterComponent
                              filter={filter}
                              dashboardId={dashboardId}
                            />
                          </Grid>
                        ))}
                    </Grid>
                    <Grid item xs={12}>
                      <Box display={'flex'} justifyContent={'flex-end'}>
                        <Button
                          onClick={() => closeSettings()}
                          className="secondary-btn"
                          color="secondary"
                          variant="contained"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => {
                            applyRegionSelection(true);
                            closeSettings();
                          }}
                          className="primary-btn"
                          color="primary"
                          variant="contained"
                        >
                          Apply
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Menu>
            )}
          </Box>
          {!mapPopupStateVisibility && (
            <>
              <Box className="relative-pos">
                <Box className="bands-main">
                  <IconButton
                    color="inherit"
                    onClick={(e) => settingsRef.current?.toggleSettings(e)}
                    className="big-button"
                  >
                    <SettingsIcon className="big-button-icon" />
                  </IconButton>
                </Box>
              </Box>
              <SettingsFilters
                ref={settingsRef}
                anchorEl={anchorEl}
                dashboardId={dashboardId}
                setAnchorEl={setAnchorEl}
                closeSettings={closeSettings}
                initialFilters={initialFilters}
                globalLayersControlRef={globalLayersControlRef}
                applySettingsClicked={applySettingsClicked}
              />
              <Box className="bands-main">
                <Tooltip title={geoJsonLayersIconDetails.tootltipText}>
                  <IconButton
                    color="inherit"
                    onClick={() => handleOnGeoJSONLayersFilterClick()}
                    className={`big-button-layers ${geoJsonLayersIconDetails.className}`}
                  >
                    <MapIcon className="big-button-icon" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box className="bands-main">
                <div>
                  <IconButton
                    color="inherit"
                    onClick={() =>
                      granularityDateRangePickerRef.current?.handleShowModal(
                        true
                      )
                    }
                    className="big-button-layers"
                  >
                    <CalendarMonthIcon className="big-button-icon" />
                  </IconButton>
                  <GranularityDateRangePicker
                    ref={granularityDateRangePickerRef}
                    granularityOptions={granularityOptions}
                    handleApplyFilter={handleGranularityDatePickerApply}
                    inputBody={granularityDates}
                    calendarFilterOptions={calendarFilterOptions}
                  />
                </div>
              </Box>
              <Box className="map-search">
                <SearchInput
                  ref={searchInputRef}
                  markersList={markersList}
                  searchSuggestionsRef={searchSuggestionsRef}
                  customGeoJsonLayers={customGeoJsonLayers}
                />
              </Box>
            </>
          )}
        </Box>

        {!mapPopupStateVisibility && (
          <Box className={'bands-filters'}>
            <Box className="bands-main">
              {[
                ...new Set(
                  bandsList.map(
                    (bandFilterItem) => bandFilterItem.id!.split('#')[0]
                  )
                ),
              ].map((technology, index) => (
                <React.Fragment key={index}>
                  <TechnologyBandSelector
                    technology={technology}
                    technologyBandsList={
                      bandsList.filter(
                        (bandFilterItem) =>
                          bandFilterItem.id!.split('#')[0] === technology
                      ) || []
                    }
                    handleBandToggle={handleBandToggle}
                  ></TechnologyBandSelector>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </div>
    </Box>
  );
};

export default forwardRef<TopFiltersRef, TopFiltersProps>(TopFilters);
